import { Card, ScrollArea, Strong, Table, Text } from '@radix-ui/themes';
import { CommonDetails } from 'components/common/details';
import { ErrorBoundary } from 'components/common/error-boundary';
import { ActivateModelButton } from 'components/sections/admin-portal/machine-models/activate-model-button';
import { MachineContext } from 'contexts/machine.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import {
  BAD_MODEL_PERFORMANCE,
  GOOD_MODEL_PERFORMANCE,
} from 'lib_ts/classes/machine-metric.helper';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { ModelPredictionHelper } from 'lib_ts/classes/model-prediction.helper';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { RADIX, RadixColor } from 'lib_ts/enums/radix-ui';
import { IEvalModelResult } from 'lib_ts/interfaces/modelling/i-eval-models';
import { FC, useContext, useMemo } from 'react';

const PRECISION_DECIMALS = 4;
const PRECISION_COEFF = Math.pow(10, PRECISION_DECIMALS);

interface INumericEntry {
  value: number;
  color: RadixColor;
}
interface IRowSummary {
  root: string;
  name: string;

  /** note: do not use in conjunction with text_values */
  num_values?: INumericEntry[];

  /** note: do not use in conjunction with num_values */
  text_values?: string[];

  // i.e. best in the context of errors
  minValue?: number;

  // i.e. worst in the context of errors
  maxValue?: number;
}

interface IProps {
  metrics: IEvalModelResult[];
}

export const SummarizeModelMetrics: FC<IProps> = (props) => {
  const { activeModel } = useContext(MachineContext);

  const attributes = useMemo(() => {
    const models = props.metrics;

    const modelValues: IRowSummary[] = [
      {
        root: 'Model',
        name: 'MachineID',
        text_values: models.map((m) => m.machineID ?? '???'),
      },
      {
        root: 'Model',
        name: 'Ball Type',
        text_values: models.map((m) => m.ball_type ?? '???'),
      },
      {
        root: 'Model',
        name: 'Break Support',
        text_values: models.map((m) => (m.supports_breaks ? 'Yes' : 'No')),
      },
      {
        root: 'Model',
        name: 'Spin Support',
        text_values: models.map((m) => (m.supports_spins ? 'Yes' : 'No')),
      },
    ];

    const BEST_LABEL = 'Best MAE 90%';
    const WORST_LABEL = 'Worst MAE 10%';

    const errorValues: IRowSummary[] = [
      {
        root: BEST_LABEL,
        name: 'vx',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_best_90p.vx;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.vx
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.vx
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };

          return result;
        }),
      },
      {
        root: BEST_LABEL,
        name: 'vy',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_best_90p.vy;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.vy
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.vy
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: BEST_LABEL,
        name: 'vz',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_best_90p.vz;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.vz
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.vz
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: BEST_LABEL,
        name: 'wx',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_best_90p.wx;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.wx
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.wx
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: BEST_LABEL,
        name: 'wy',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_best_90p.wy;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.wy
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.wy
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: BEST_LABEL,
        name: 'wz',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_best_90p.wz;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.wz
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.wz
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: BEST_LABEL,
        name: 'break_x_in',
        num_values: models.map((m) => {
          const inchValue =
            m.model_performance.mean_best_90p.break_x_ft * FT_TO_INCHES;

          const result: INumericEntry = {
            value: inchValue,
            color:
              inchValue > BAD_MODEL_PERFORMANCE.break_x_ft * FT_TO_INCHES
                ? RADIX.COLOR.DANGER
                : inchValue < GOOD_MODEL_PERFORMANCE.break_x_ft * FT_TO_INCHES
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: BEST_LABEL,
        name: 'break_z_in',
        num_values: models.map((m) => {
          const inchValue =
            m.model_performance.mean_best_90p.break_z_ft * FT_TO_INCHES;

          const result: INumericEntry = {
            value: inchValue,
            color:
              inchValue > BAD_MODEL_PERFORMANCE.break_z_ft * FT_TO_INCHES
                ? RADIX.COLOR.DANGER
                : inchValue < GOOD_MODEL_PERFORMANCE.break_z_ft * FT_TO_INCHES
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: WORST_LABEL,
        name: 'vx',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_worst_10p.vx;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.vx
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.vx
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: WORST_LABEL,
        name: 'vy',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_worst_10p.vy;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.vy
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.vy
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: WORST_LABEL,
        name: 'vz',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_worst_10p.vz;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.vz
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.vz
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: WORST_LABEL,
        name: 'wx',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_worst_10p.wx;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.wx
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.wx
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: WORST_LABEL,
        name: 'wy',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_worst_10p.wy;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.wy
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.wy
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: WORST_LABEL,
        name: 'wz',
        num_values: models.map((m) => {
          const v = m.model_performance.mean_worst_10p.wz;
          const result: INumericEntry = {
            value: v,
            color:
              v > BAD_MODEL_PERFORMANCE.wz
                ? RADIX.COLOR.DANGER
                : v < GOOD_MODEL_PERFORMANCE.wz
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: WORST_LABEL,
        name: 'break_x_in',
        num_values: models.map((m) => {
          const inchValue =
            m.model_performance.mean_worst_10p.break_x_ft * FT_TO_INCHES;

          const result: INumericEntry = {
            value: inchValue,
            color:
              inchValue > BAD_MODEL_PERFORMANCE.break_x_ft * FT_TO_INCHES
                ? RADIX.COLOR.DANGER
                : inchValue < GOOD_MODEL_PERFORMANCE.break_x_ft * FT_TO_INCHES
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
      {
        root: WORST_LABEL,
        name: 'break_z_in',
        num_values: models.map((m) => {
          const inchValue =
            m.model_performance.mean_worst_10p.break_z_ft * FT_TO_INCHES;
          const result: INumericEntry = {
            value: inchValue,
            color:
              inchValue > BAD_MODEL_PERFORMANCE.break_z_ft * FT_TO_INCHES
                ? RADIX.COLOR.DANGER
                : inchValue < GOOD_MODEL_PERFORMANCE.break_z_ft * FT_TO_INCHES
                  ? RADIX.COLOR.SUCCESS
                  : RADIX.COLOR.WARNING,
          };
          return result;
        }),
      },
    ];

    /** for errors, smaller is better */
    errorValues.forEach((e) => {
      if (!e.num_values) {
        return;
      }

      e.num_values.forEach(
        (v) =>
          (v.value = Math.round(v.value * PRECISION_COEFF) / PRECISION_COEFF)
      );

      if (e.num_values.length > 0) {
        e.minValue = MiscHelper.getMin(e.num_values.map((v) => v.value));
        e.maxValue = MiscHelper.getMax(e.num_values.map((v) => v.value));
      }
    });

    const ratingValues: IRowSummary[] = [
      {
        root: '%',
        name: 'Model Performance',
        num_values: models.map((m) => {
          const result: INumericEntry = {
            value: Math.round(
              ModelPredictionHelper.overallPercent(m.model_performance) * 100
            ),
            color: RADIX.COLOR.NEUTRAL,
          };
          return result;
        }),
      },
    ];

    /** for ratings, bigger is better */
    ratingValues.forEach((r) => {
      if (!r.num_values) {
        return;
      }

      if (r.num_values.length > 0) {
        r.minValue = MiscHelper.getMax(r.num_values.map((v) => v.value));
        r.maxValue = MiscHelper.getMin(r.num_values.map((v) => v.value));
      }
    });

    return [
      ...modelValues,
      ...errorValues,
      ...ratingValues,
      // additional stats can go here, e.g. if larger is better for those values
    ];
  }, [props.metrics]);

  return (
    <ErrorBoundary identifier="SummarizeMetrics">
      <Card>
        <CommonDetails summary="Summary" defaultOpen>
          <ScrollArea scrollbars="horizontal">
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Attribute</Table.ColumnHeaderCell>

                  {props.metrics.map((m) => (
                    <Table.ColumnHeaderCell key={`model-type-${m.model_id}`}>
                      {/* mark the current model somehow */}
                      {m.model_type}{' '}
                      {m.model_id === activeModel?._id
                        ? '(Current)'
                        : undefined}
                    </Table.ColumnHeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body className="font-mono">
                <Text size={RADIX.TEXT.SIZE.XS}></Text>
                <Table.Row>
                  <Table.ColumnHeaderCell>Model Name</Table.ColumnHeaderCell>

                  {props.metrics.map((m) => {
                    const datestamp = format(
                      parseISO(m.created),
                      LOCAL_DATETIME_FORMAT,
                      {
                        timeZone: LOCAL_TIMEZONE,
                      }
                    );

                    return (
                      <Table.ColumnHeaderCell
                        key={`model-name-${m.model_id}`}
                        title={`${m.model_name}\n\nCreated on ${datestamp}`}
                      >
                        {m.model_name}
                      </Table.ColumnHeaderCell>
                    );
                  })}
                </Table.Row>

                {attributes.map((row, iRow) => (
                  <Table.Row key={iRow}>
                    <Table.Cell>
                      {row.root} {row.name}
                    </Table.Cell>

                    {row.num_values &&
                      row.num_values.map((entry, iValue) => {
                        const isMinimum =
                          row.minValue && entry.value <= row.minValue;

                        return (
                          <Table.Cell
                            key={`row-${iRow}-value-${iValue}`}
                            align="right"
                          >
                            {isMinimum ? (
                              <Text color={entry.color}>
                                <Strong>{entry.value}</Strong>
                              </Text>
                            ) : (
                              <Text color={entry.color}>{entry.value}</Text>
                            )}
                          </Table.Cell>
                        );
                      })}

                    {row.text_values &&
                      row.text_values.map((value, iValue) => {
                        return (
                          <Table.Cell
                            key={`row-${iRow}-value-${iValue}`}
                            align="right"
                          >
                            {value}
                          </Table.Cell>
                        );
                      })}
                  </Table.Row>
                ))}
              </Table.Body>
              <tfoot>
                <Table.Row>
                  <Table.Cell>&nbsp;</Table.Cell>

                  {props.metrics.map((m) => (
                    <Table.Cell
                      key={`activate-model-${m.model_id}`}
                      align="right"
                    >
                      {m.model_id !== activeModel?._id && (
                        <ActivateModelButton modelID={m.model_id} />
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              </tfoot>
            </Table.Root>
          </ScrollArea>
        </CommonDetails>
      </Card>
    </ErrorBoundary>
  );
};
